import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Banknote, CreditCard, ShoppingBag, ArrowLeft, TicketPercent, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { fmtPrice, FREE_SHIPPING, SHIPPING_COST } from "../lib/shop";

const FIELDS = [
  { key: "name", label: "Nume complet", placeholder: "Ex: Andrei Popescu", type: "text" },
  { key: "email", label: "Email", placeholder: "email@exemplu.ro", type: "email" },
  { key: "phone", label: "Telefon", placeholder: "07xx xxx xxx", type: "tel" },
  { key: "address", label: "Adresă (stradă, număr, bloc)", placeholder: "Str. Exemplu nr. 10", type: "text" },
  { key: "city", label: "Oraș", placeholder: "București", type: "text" },
  { key: "county", label: "Județ", placeholder: "Ilfov", type: "text" },
];

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", county: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
    }
  }, [user]);

  const baseShipping = subtotal >= FREE_SHIPPING || (items.length > 0 && items.every((i) => i.free_shipping)) ? 0 : SHIPPING_COST;
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);
  const shipping = coupon ? 0 : baseShipping;
  const total = subtotal + shipping;

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) return;
    if (!form.email.trim()) {
      toast.error("Completează întâi emailul, apoi aplică codul de reducere");
      return;
    }
    setCouponBusy(true);
    try {
      const { data } = await api.post("/coupons/validate", { code, email: form.email.trim() });
      setCoupon(data.code);
      toast.success(`Codul ${data.code} a fost aplicat — transport gratuit!`);
    } catch (err) {
      setCoupon(null);
      toast.error(apiError(err, "Codul nu a putut fi aplicat"));
    } finally {
      setCouponBusy(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponInput("");
  };

  const submit = async (e) => {
    e.preventDefault();
    for (const f of FIELDS) {
      if (!form[f.key].trim()) {
        toast.error(`Completează câmpul „${f.label}"`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.id, qty: i.qty, note: i.note || "" })),
        customer: form,
        coupon_code: coupon || "",
      });
      setOrder(data);
      clear();
      toast.success(`Comanda ${data.number} a fost plasată!`);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(apiError(err, "Comanda nu a putut fi plasată. Încearcă din nou."));
    } finally {
      setSubmitting(false);
    }
  };

  if (order) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-20 sm:px-6" data-testid="order-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="rounded-lg border border-line bg-white p-10 text-center shadow-xl shadow-navy/5"
        >
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.2 }}
            className="mx-auto flex h-20 w-20 items-center justify-center rounded-lg bg-skytint"
          >
            <CheckCircle2 className="h-10 w-10 text-navy" />
          </motion.span>
          <h1 className="mt-6 font-display text-3xl font-extrabold text-navy">Comandă plasată cu succes!</h1>
          <p className="mt-2 text-muted2">
            Numărul comenzii tale este <span className="font-bold text-navy" data-testid="order-number">{order.number}</span>
          </p>
          <div className="mt-6 rounded-2xl bg-skytint p-5 text-left text-sm">
            <div className="flex justify-between py-1"><span className="text-muted2">Subtotal</span><span className="font-semibold">{fmtPrice(order.subtotal)}</span></div>
            <div className="flex justify-between py-1"><span className="text-muted2">Livrare</span><span className="font-semibold">{order.shipping === 0 ? "Gratuită" : fmtPrice(order.shipping)}</span></div>
            {order.coupon_code && (
              <div className="flex justify-between py-1"><span className="text-muted2">Cupon aplicat</span><span className="font-bold text-emerald-600" data-testid="order-coupon">{order.coupon_code}</span></div>
            )}
            <div className="mt-2 flex justify-between border-t border-line pt-3"><span className="font-bold">Total de plată (ramburs)</span><span className="font-display text-lg font-extrabold text-navy">{fmtPrice(order.total)}</span></div>
          </div>
          <p className="mt-5 flex items-center justify-center gap-2 text-sm font-semibold text-muted2">
            <Banknote className="h-4 w-4 text-navy" />
            Vei plăti ramburs, direct la curier, la livrare.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/produse" className="rounded-lg bg-navy px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-navy" data-testid="order-success-continue">
              Continuă cumpărăturile
            </Link>
            {user && (
              <Link to="/cont" className="rounded-full border border-line px-7 py-3.5 text-sm font-bold text-slate-700 transition-colors hover:border-sky2 hover:text-navy" data-testid="order-success-account">
                Vezi comenzile mele
              </Link>
            )}
          </div>
        </motion.div>
      </main>
    );
  }

  if (items.length === 0) {
    return (
      <main className="mx-auto flex max-w-7xl flex-col items-center gap-4 px-4 py-32 text-center" data-testid="checkout-empty">
        <span className="flex h-16 w-16 items-center justify-center rounded-lg bg-foam">
          <ShoppingBag className="h-7 w-7 text-sky-600" />
        </span>
        <h1 className="font-display text-2xl font-bold text-navy">Coșul tău este gol</h1>
        <p className="text-sm text-muted2">Adaugă produse înainte de a finaliza comanda.</p>
        <Link to="/produse" className="mt-2 flex items-center gap-2 rounded-lg bg-navy px-7 py-3.5 text-sm font-bold text-white hover:bg-navy" data-testid="checkout-empty-shop-link">
          <ArrowLeft className="h-4 w-4" /> Înapoi la produse
        </Link>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8" data-testid="checkout-page">
      <h1 className="font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">Finalizează comanda</h1>
      <p className="mt-2 text-sm text-muted2">Fără cont necesar — completezi datele, noi livrăm, tu plătești la curier.</p>

      <form onSubmit={submit} className="mt-10 grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <div className="rounded-lg border border-line bg-white p-8">
            <h2 className="font-display text-lg font-bold text-navy">Date de livrare</h2>
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              {FIELDS.map((f) => (
                <div key={f.key} className={f.key === "address" ? "sm:col-span-2" : ""}>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-muted2">{f.label}</label>
                  <input
                    type={f.type}
                    value={form[f.key]}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    className="w-full rounded-lg border border-line px-4 py-3 text-sm outline-none transition-colors focus:border-sky2 focus:ring-2 focus:ring-sky2/20"
                    data-testid={`checkout-${f.key}-input`}
                  />
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-muted2">Observații (opțional)</label>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Detalii pentru curier, interval orar preferat..."
                  className="w-full resize-none rounded-lg border border-line px-4 py-3 text-sm outline-none transition-colors focus:border-sky2 focus:ring-2 focus:ring-sky2/20"
                  data-testid="checkout-notes-input"
                />
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-lg border border-line bg-white p-8">
            <h2 className="font-display text-lg font-bold text-navy">Metodă de plată</h2>
            <div className="mt-5 flex flex-col gap-3">
              <div className="flex items-center gap-4 rounded-2xl border-2 border-sky2 bg-skytint/60 p-5" data-testid="payment-ramburs">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-navy">
                  <Banknote className="h-5 w-5 text-white" />
                </span>
                <div>
                  <p className="text-sm font-bold text-navy">Plată la livrare (ramburs)</p>
                  <p className="text-xs text-muted2">Plătești cash sau cu cardul la curier, când primești coletul.</p>
                </div>
                <CheckCircle2 className="ml-auto h-5 w-5 shrink-0 text-navy" />
              </div>
              <div className="flex items-center gap-4 rounded-2xl border border-dashed border-line p-5 opacity-60" data-testid="payment-card-soon">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-skytint">
                  <CreditCard className="h-5 w-5 text-sky-light" />
                </span>
                <div>
                  <p className="text-sm font-bold text-muted2">Plată online cu cardul</p>
                  <p className="text-xs text-sky-light">În curând — integrare Netopia în lucru.</p>
                </div>
                <span className="ml-auto rounded-lg bg-skytint px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-muted2">În curând</span>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="sticky top-32 rounded-lg border border-line bg-white p-8" data-testid="checkout-summary">
            <h2 className="font-display text-lg font-bold text-navy">Sumar comandă</h2>
            <ul className="mt-5 flex max-h-64 flex-col gap-4 overflow-y-auto pr-1">
              {items.map((i) => (
                <li key={i.key || i.id} className="flex items-center gap-3">
                  <img src={i.image} alt={i.name} className="h-14 w-14 rounded-lg border border-skytint object-cover" />
                  <div className="flex-1">
                    <p className="text-sm font-bold leading-snug text-navy">{i.name}</p>
                    <p className="text-xs text-sky-light">{i.qty} × {fmtPrice(i.price)}</p>
                    {i.note && <p className="mt-0.5 text-xs italic text-muted2">{i.note}</p>}
                  </div>
                  <span className="text-sm font-bold text-navy">{fmtPrice(i.price * i.qty)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 border-t border-skytint pt-5 text-sm">
              <div className="flex justify-between py-1"><span className="text-muted2">Subtotal</span><span className="font-semibold" data-testid="summary-subtotal">{fmtPrice(subtotal)}</span></div>
              {coupon ? (
                <div className="flex items-center justify-between gap-2 rounded-lg bg-emerald-50 px-3 py-2" data-testid="coupon-applied">
                  <span className="flex items-center gap-2 text-xs font-bold text-emerald-700">
                    <TicketPercent className="h-4 w-4" /> {coupon} — transport gratuit
                  </span>
                  <button type="button" onClick={removeCoupon} className="text-emerald-500 transition-colors hover:text-rose-500" aria-label="Elimină codul" data-testid="coupon-remove-button">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                    placeholder="Cod de reducere"
                    className="flex-1 rounded-lg border border-line px-3 py-2 text-xs font-bold uppercase tracking-wider outline-none transition-colors focus:border-sky2 focus:ring-2 focus:ring-sky2/20"
                    data-testid="coupon-input"
                  />
                  <button
                    type="button"
                    onClick={applyCoupon}
                    disabled={couponBusy || !couponInput.trim()}
                    className="rounded-lg border border-line px-4 py-2 text-xs font-bold text-navy transition-colors hover:border-sky2 hover:bg-skytint disabled:opacity-50"
                    data-testid="coupon-apply-button"
                  >
                    {couponBusy ? "Se verifică..." : "Aplică"}
                  </button>
                </div>
              )}
              <div className="flex justify-between py-1">
                <span className="text-muted2">Livrare</span>
                <span className={`font-semibold ${shipping === 0 ? "text-navy" : ""}`} data-testid="summary-shipping">
                  {shipping === 0 ? "Gratuită" : fmtPrice(shipping)}
                  {coupon && baseShipping > 0 && <span className="ml-2 text-muted2 line-through">{fmtPrice(baseShipping)}</span>}
                </span>
              </div>
              <div className="mt-3 flex justify-between border-t border-skytint pt-4">
                <span className="font-bold text-navy">Total</span>
                <span className="font-display text-2xl font-extrabold text-navy" data-testid="summary-total">{fmtPrice(total)}</span>
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="mt-7 w-full rounded-lg bg-navy py-4 text-sm font-bold text-white transition-all duration-300 hover:bg-navy-dark hover:shadow-xl hover:shadow-navy/30 disabled:opacity-60"
              data-testid="checkout-submit-button"
            >
              {submitting ? "Se trimite comanda..." : `Plasează comanda — ${fmtPrice(total)}`}
            </button>
            <p className="mt-3 text-center text-[11px] text-muted2">
              Prin plasarea comenzii ești de acord cu <Link to="/legal#termeni" className="font-bold text-navy underline" data-testid="checkout-terms-link">Termenii și condițiile</Link> și <Link to="/legal#gdpr" className="font-bold text-navy underline" data-testid="checkout-gdpr-link">Politica de confidențialitate</Link>. Toate prețurile sunt în lei (RON) și includ TVA.
            </p>
          </div>
        </div>
      </form>
    </main>
  );
}
